import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation, withTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import compose from 'ramda/src/compose';
import { Box, Divider, Link, Typography } from '@material-ui/core';
import LocaleKeys from '../../locales/keys';
import { useRetrieveDefaultPayment } from '../../common/hooks/useRetrieveDefaultPayment';
import useRetrieveProductContext from '../../common/hooks/useRetrieveProductContext';
import useProductData from '../../common/hooks/useProductData';
import useCalculateOrderQuote from '../../common/hooks/useCalculateOrderQuote';
import useConfigCurrencyJSON from '../../common/hooks/useConfigCurrencyJSON';
import useRetrieve3DSToken from '../../common/hooks/useRetrieve3DSToken';
import ErrorScreen from '../../common/errorScreen';
import VoucherCode from '../components/voucherCode';
import AppliedVoucherCode from '../components/appliedVoucherCode';
import OrderDetailsContainer from './OrderDetailsContainer';
import {
  voucherName,
  errorCodeConstant,
} from '../../constants/checkout.constant';
import {
  getPaymentInstrumentListNode,
  getCOQVoucherCodePayload,
  setSubmitOrderUpdateCountryPayload,
  getSubmitOrderResponseContextData,
  getDefaultShoppingCart,
  shouldWeShowAdditionalSubscriberDetails,
  getUpdateSubscriberApiPayload,
  getExistingSubscriptionForUpgrade,
  getUpgradePlanState,
} from '../helpers/paymentOrderHelpers';
import NoOrderablePricingPlans from '../components/NoOrderablePricingPlans';
import { getOrderQuoteUtilities } from '../helpers/paymentOrderHelpers';
import {
  getTranslatedOrderItemsList,
  getGenericTranslatedOrderItems,
  getSetup3DSWeb,
  getInitialize3DSWeb,
  getThreeDSChallengeHandler,
} from '../helpers/paymentOrderHelpers';
import PaymentMethodContainer from './PaymentMethodContainer';
import useSubmitOrderUpdateCountry from '../../common/hooks/useSubmitOrderUpdateCountry';
import useMetaDataCodeTables from '../../common/hooks/useMetaDataCodeTables';
import useUpdateSubscriber from '../../common/hooks/useUpdateSubscriber';
import { payloadTypes } from '../../constants/metaDataPayloadTypes';
import { useRetrieveSubscriber } from '../../common/hooks/useRetrieveSubscriber';
import {
  getLeadSourceValue,
  paymentInstrumentTypes,
  readSubscriberCountry,
} from '../../utils/common';
import { translateFault } from '../../helpers/faults';
import { postMessage } from '../../utils/postMessage';
import FreeTrialHeader from './FreeTrialHeader';
import SubscriptionCheck from '../../common/SubscriptionCheck';
import useStripePaymentList from '../../common/hooks/useStripePaymentList';
import { Backdrop } from '../../app/components/backdrop';
import FirstNameField from '../../common/forms/fields/components/firstNameField';
import LastNameField from '../../common/forms/fields/components/lastNameField';
import DateOfBirthField from '../../common/forms/fields/components/dateOfBirthField';
import * as Yup from 'yup';
import {
  firstName,
  lastName,
  dateOfBirth,
} from '../../common/forms/validationSchemas';
import { formatDateString } from '../../utils/common';
import useSubscription from '../../common/hooks/useSubscription';
import useUpdateMultipleSubscriptions from '../../common/hooks/useUpdateMultipleSubscriptions';
import useRemoveMultiplePaymentInstruments from '../../common/hooks/useRemoveMultiplePaymentInstruments';
import { useHistory } from 'react-router-dom';
import OrderDetailsUpgradePricingPlan from '../components/orderDetailsUpgradePricingPlan';
import backArrow from '../../images/icon-back-arrow.svg';
import { PURCHASE } from '../../constants/upgradeJourney.constant';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: '100%',
    backgroundColor: theme.palette.background.paper,
  },
  orderPanelHeading: {
    marginBottom: '10px',
    padding: '10px 15px',
  },
  headerText: {
    borderBottom: `1px solid ${theme.palette.custom.divider}`,
    textTransform: 'uppercase',
    paddingBottom: '15px',
  },
  divider: {
    border: `1px  solid ${theme.palette.custom.divider}`,
    margin: '20px 0',
  },
  hidden: {
    display: 'none',
  },
}));

function CheckoutContainer(props) {
  const {
    stripePromise,
    userCountry,
    staticApiKey,
    staticApiUrl,
    productExternalReference,
    productExternalReferenceType,
    pricingPlanExternalReference,
    cdStripeHeader,
  } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const language = useSelector((state) => state.ascendon.settings.language);
  const subscriber = useSelector(
    (state) => state?.ascendon?.subscriberAPI?.RetrieveSubscriber?.Subscriber,
  );

  const { location, push } = useHistory();

  const upgradePlanState = getUpgradePlanState(location?.state);

  /** web api hooks */

  const [subscriptionFault, setSubscriptionFault] = useState(null);

  const handleFaultFromChild = (fault) => {
    setSubscriptionFault(fault);
  };

  const [
    requestProductDataUiModel,
    requestProductDataFault,
    requestProductData,
  ] = useProductData();

  const [
    retrieveDefaultPaymentResponse,
    retrieveDefaultPaymentFault,
    requestRetrieveDefaultPayment,
  ] = useRetrieveDefaultPayment();

  const [
    retrieveProductContextResponse,
    retrieveProductContextFault,
    requestRetrieveProductContext,
  ] = useRetrieveProductContext();

  const [
    calculateOrderQuoteResponse,
    calculateOrderQuoteFault,
    calculateOrderQuoteFaultCode,
    requestCalculateOrderQuote,
  ] = useCalculateOrderQuote();

  const [
    retrieveConfigCurrencyJSONResponse,
    retrieveEnvironment,
    retrieveConfigCurrencyJSONFault,
    requestConfigCurrencyJSON,
  ] = useConfigCurrencyJSON();

  const [
    retrieve3DSTokenResponse,
    retrieve3DSTokenFault,
    requestRetrieve3DSToken,
  ] = useRetrieve3DSToken();

  const [
    submitOrderUpdateCountryResponse,
    submitOrderUpdateCountryFault,
    submitOrderUpdateCountryLoading,
    requestSubmitOrderUpdateCountry,
    submitOrderInteractionData,
  ] = useSubmitOrderUpdateCountry();

  const [
    retrieveMetaDataCodeTablesResponse,
    retrieveMetaDataCodeTablesFault,
    requestMetaDataCodeTables,
  ] = useMetaDataCodeTables();

  const [
    retrieveStripePaymentListUiModel,
    retrieveStripePaymentListFault,
    requestStripePaymentList,
  ] = useStripePaymentList();

  const [
    updateSubscriberResponse,
    updateSubscriberFault,
    requestUpdateSubscriber,
  ] = useUpdateSubscriber();

  const [
    retrieveSubscriberResponse,
    retrieveSubscriberFault,
    requestRetrieveSubscriber,
  ] = useRetrieveSubscriber();

  const [
    updateMultipleSubscriptionUiModel,
    updateMultipleSubscriptionFault,
    requestUpdateMultipleSubscriptions,
  ] = useUpdateMultipleSubscriptions();

  const [
    removeMultiplePaymentInstrumentsUiModel,
    removeMultiplePaymentInstrumentsFault,
    requestRemoveMultiplePaymentInstruments,
  ] = useRemoveMultiplePaymentInstruments();

  const [subscriptionUiModel, subscriptionUiFault, requestSubscription] =
    useSubscription();

  const AdditionalSubscriberDetailsSchema = Yup.object().shape({
    FirstName: firstName('firstName', language),
    LastName: lastName('lastName', language),
    DateOfBirth: dateOfBirth('dateOfBirth', language),
  });

  const emptyValueFirstName = 'Subscriber';
  const emptyValuesLastName = ['Lastname', 'Surname'];

  /** use state */
  const [errors, setErrors] = useState([]);
  const [errorFaultCode, setErrorFaultCode] = useState(null);
  const [hasRPCresponse, setHasRPCresponse] = useState(false);
  const [hasOrderablePricingPlans, setHasOrderablePricingPlans] =
    useState(false);
  const [shoppingCartList, setShoppingCartList] = useState({});
  const [redemptionCodesPayload, setRedemptionCodesPayload] = useState([]);
  const [voucherCodeCredentialError, setVoucherCodeCredentialError] =
    useState('');
  const [disableVoucherCode, setDisableVoucherCode] = useState(false);
  const [showVoucherCode, setShowVoucherCode] = useState(true);
  const [appliedCouponsText, setAppliedCouponsText] = useState('');
  const [translatedOrderItems, setTranslatedOrderItems] = useState([]);
  const [hasSuccessFulCOQResponse, setHasSuccessFulCOQResponse] =
    useState(null);
  const [defaultPaymentResponse, setDefaultPaymentResponse] = useState({
    paymentInstrumentIds: [],
    isPaymentInstrumentIdUpdate: false,
  });
  const [
    calculateOrderQuoteResponseForPayment,
    setCalculateOrderQuoteResponseForPayment,
  ] = useState(false);
  const [
    calculateOrderQuoteFaultNodeForPayment,
    setCalculateOrderQuoteFaultNodeForPayment,
  ] = useState(false);
  const [isErrorAlertDisplay, setIsErrorAlertDisplay] = useState(false);
  const [isGenericErrorAlertDisplay, setIsGenericErrorAlertDisplay] =
    useState(false);
  useState(false);
  const [addressAndCodeTableFault, setAddressAndCodeTableFault] =
    useState(false);
  const [isCreatePaymentInstrument, setIsCreatePaymentInstrument] =
    useState(false);
  const [isCoqResponseShowLoader, setIsCoqResponseShowLoader] = useState(false);
  const [
    createPaymentInstrumentCoqResponseStatus,
    setCreatePaymentInstrumentCoqResponseStatus,
  ] = useState(false);
  const [
    submitOrderCountryUpdateFaultMessage,
    setSubmitOrderCountryUpdateFaultMessage,
  ] = useState('');

  const [isMerchantEventHandlerFault, setIsMerchantEventHandlerFault] =
    useState(false);

  const [isVoucherCodeError, setIsVoucherCodeError] = useState(false);
  const [orderablePricingPlanId, setOrderablePricingPlanId] = useState(null);

  const [subscriptionCheckInProgress, setSubscriptionCheckInProgress] =
    useState(false);
  const [isReady, setIsReady] = useState(false);
  const [defaultShoppingCartQuote, setDefaultShoppingCartQuote] =
    useState(null);
  const [orderComplete, setOrderComplete] = useState(false);
  const [defaultValidPI, setDefaultValidPI] = useState(null);
  const [showBackdrop, setShowBackdrop] = useState(false);
  const [showAdditionalSubscriberDetails, setShowAdditionalSubscriberDetails] =
    useState(null);
  const [updateSubscriberFaultMsg, setUpdateSubscriberFaultMsg] = useState('');
  const [
    submitStripeOrNonStripeCardErrorStatus,
    setSubmitStripeOrNonStripeCardErrorStatus,
  ] = useState(false);
  const [sendUpdateSubscriberResponse, setSendUpdateSubscriberResponse] =
    useState(true);
  const [completeOrder3DSstatus, setCompleteOrder3DSstatus] = useState(false);
  const [isPaymentMethodFormError, setIsPaymentMethodFormError] =
    useState(false);
  const [isPaymentReady, setIsPaymentReady] = useState(false);
  const [currentSubscriberData, setCurrentSubscriberData] = useState(undefined);
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [stripePaymentListUiModel, setStripePaymentListUiModel] = useState({});

  const requestFaults = [
    requestProductDataFault,
    retrieveDefaultPaymentFault,
    retrieveProductContextFault,
    retrieveConfigCurrencyJSONFault,
    retrieve3DSTokenFault,
    retrieveMetaDataCodeTablesFault,
    retrieveSubscriberFault,
  ];

  const fieldInitialValueHandler = (fieldInitialValue, fieldName) => {
    let fieldValue;
    if (fieldName === 'firstName') {
      fieldValue =
        fieldInitialValue?.length !== 1 &&
        fieldInitialValue !== emptyValueFirstName
          ? fieldInitialValue
          : '';
    } else if (fieldName === 'lastName') {
      fieldValue =
        fieldInitialValue?.length !== 1 &&
        emptyValuesLastName.indexOf(fieldInitialValue) === -1
          ? fieldInitialValue
          : '';
    } else {
      fieldValue = formatDateString(fieldInitialValue) || '';
    }

    return fieldValue;
  };

  const formik = useFormik({
    initialValues: {
      FirstName:
        subscriber &&
        fieldInitialValueHandler(subscriber?.FirstName, 'firstName'),
      LastName:
        subscriber &&
        fieldInitialValueHandler(subscriber?.LastName, 'lastName'),
      DateOfBirth:
        subscriber && fieldInitialValueHandler(subscriber?.BirthDate),
    },
    enableReinitialize: true,
    validationSchema: AdditionalSubscriberDetailsSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: () => {
      if (submitStripeOrNonStripeCardErrorStatus) {
        return;
      }
      const Subscriber = {
        ...currentSubscriberData,
        ...getUpdateSubscriberApiPayload(formik, language),
      };
      postMessage('module.showLoader', {});
      requestUpdateSubscriber({ Subscriber });
    },
  });

  useEffect(() => {
    if (isPaymentMethodFormError) {
      formik.handleSubmit();
    }
  }, [isPaymentMethodFormError]);

  useEffect(() => {
    if (formik?.errors && JSON.stringify(formik?.errors) !== '{}') {
      setIsPaymentMethodFormError(true);
    }
  }, [formik?.errors]);

  /** Helper functions */

  const handleVoucherCancelClick = () => {
    if (redemptionCodesPayload.length) {
      handleVoucherSaveClick({ values: { voucherCode: '' } });
    }
    setVoucherCodeCredentialError('');
    setIsVoucherCodeError(false);
  };

  const handleVoucherSaveClick = (formEvent) => {
    const { values } = formEvent;
    const { voucherCode } = values;
    const { ShoppingCart } = shoppingCartList;

    const { RedemptionCodes: redemptionCodesPayload, ...rest } =
      getCOQVoucherCodePayload(voucherCode, ShoppingCart);
    setRedemptionCodesPayload(redemptionCodesPayload);
    requestCalculateOrderQuote({
      redemptionCodesPayload,
      paymentInstruments: defaultPaymentResponse?.paymentInstrumentIds,
      isUpgrade: upgradePlanState?.isUpgradePlan,
      ...getExistingSubscriptionForUpgrade(upgradePlanState),
      ...rest,
    });
  };

  const removeAppliedVoucherCode = () => {
    handleVoucherSaveClick({ values: { voucherCode: '' } });
  };

  const createPaymentInstrumentRequestDefault = (
    isSuccessfulResponseStatus,
  ) => {
    if (isCreatePaymentInstrument) {
      requestRetrieveDefaultPayment({ EnforceSessionCountry: false });
      setCreatePaymentInstrumentCoqResponseStatus(isSuccessfulResponseStatus);
    }
  };

  const calculateOrderQuoteResponseList = (calculateOrderQuoteResponse) => {
    if (calculateOrderQuoteResponse) {
      const {
        orderQuote: {
          Quote: { AppliedCoupons },
        },
      } = calculateOrderQuoteResponse;
      if (defaultPaymentResponse.isPaymentInstrumentIdUpdate) {
        setCalculateOrderQuoteResponseForPayment(calculateOrderQuoteResponse);
      }
      createPaymentInstrumentRequestDefault(true);
      setShowVoucherCode(!AppliedCoupons);
      const getAppliedCouponsText = AppliedCoupons || '';
      setAppliedCouponsText(getAppliedCouponsText);
      setErrors([]);
      setErrorFaultCode('');
      setIsErrorAlertDisplay(false);
    }
  };

  const calculateOrderQuoteFaultNode = (
    calculateOrderQuoteFault,
    calculateOrderQuoteFaultCode,
  ) => {
    if (defaultPaymentResponse.isPaymentInstrumentIdUpdate) {
      setCalculateOrderQuoteFaultNodeForPayment(calculateOrderQuoteFault);
    }
    createPaymentInstrumentRequestDefault(false);
    setErrorFaultCode(calculateOrderQuoteFaultCode);
    setErrors((prevState) => [...prevState, calculateOrderQuoteFault]);
  };

  const submitOrderPayloadHandler = (isSecondSubmitOrder) => {
    let submitOrderPayload;
    const { ShoppingCart } = shoppingCartList;
    const country = readSubscriberCountry();

    const getSubmitOrderUpdateCountryPayload = (leadSourceValue) => {
      const submitOrderUpdateCountryPayload =
        setSubmitOrderUpdateCountryPayload({
          redemptionCodesPayload,
          defaultPaymentResponse,
          ShoppingCart,
          country,
          leadSourceValue,
          isSecondSubmitOrder,
        });
      return submitOrderUpdateCountryPayload;
    };

    if (!isSecondSubmitOrder) {
      const leadSourceResponse =
        retrieveMetaDataCodeTablesResponse?.leadSources?.data?.Codes;
      const leadSourceValue = getLeadSourceValue(leadSourceResponse);
      submitOrderPayload = getSubmitOrderUpdateCountryPayload(leadSourceValue);
    } else {
      const leadSourceValue = undefined;
      submitOrderPayload = getSubmitOrderUpdateCountryPayload(leadSourceValue);
    }
    return submitOrderPayload;
  };

  const submitOrderHandler = () => {
    const submitOrderUpdateCountryPayload = submitOrderPayloadHandler(false);
    return submitOrderUpdateCountryPayload;
  };

  const submitOrder3DSHandler = () => {
    setIsMerchantEventHandlerFault(false);
    const submitOrderUpdateCountryPayload = submitOrderPayloadHandler(true);
    return submitOrderUpdateCountryPayload;
  };

  const requestProductDataAndMetaData = (
    isProductData,
    isMetaDataCodeTable,
  ) => {
    const { subscriberLeadSourceCode } = payloadTypes;
    if (isProductData) {
      const getProductExternalReference =
        upgradePlanState?.productExternalReference || productExternalReference;
      const getProductExternalReferenceType =
        upgradePlanState?.productExternalReferenceType ||
        productExternalReferenceType;
      requestProductData({
        productExternalReference: getProductExternalReference,
        productExternalReferenceType: getProductExternalReferenceType,
      });
    }
    if (isMetaDataCodeTable) {
      requestMetaDataCodeTables({
        subscriberLeadSourceCode,
      });
    }
  };

  const defaultSubscriptionHandlers = () => {
    // setHasOrderablePricingPlans(false);
    setHasOrderablePricingPlans(true);
    postMessage('noAvailablePurchases');
    postMessage('module.hideLoader', {});
    setIsReady(true);
  };

  /**use Effect */

  /**Initial Load */
  useEffect(() => {
    postMessage('module.showLoader', {});
    requestStripePaymentList({ cdStripeHeader });
    requestRetrieveDefaultPayment({ EnforceSessionCountry: false });
    requestSubscription();
    requestProductDataAndMetaData(true, true);
  }, []);

  useEffect(() => {
    if (subscriptionUiModel) {
      setAllSubscriptions(subscriptionUiModel?.details);
    }
  }, [subscriptionUiModel]);

  useEffect(() => {
    if (!subscriber) {
      requestRetrieveSubscriber();
      return;
    }
  }, [!subscriber]);

  useEffect(() => {
    if (submitOrderUpdateCountryLoading) {
      postMessage('module.showLoader', {});
    } else if (submitOrderUpdateCountryLoading === false) {
      // postMessage('module.hideLoader', {});
    }
  }, [submitOrderUpdateCountryLoading]);

  const { defaultPaymentConfiguration } = ASCENDON_ENV_CONFIG?.stripe;
  const defaultStripePaymentListUiModel = {
    StripePayment: {
      ConfigurationId: defaultPaymentConfiguration?.id,
      PaymentTypes: defaultPaymentConfiguration?.paymentTypes,
    },
  };

  useEffect(() => {
    if (retrieveStripePaymentListUiModel) {
      if (retrieveStripePaymentListUiModel?.StripePayment?.ConfigurationId) {
        setStripePaymentListUiModel(retrieveStripePaymentListUiModel);
      } else {
        setStripePaymentListUiModel(defaultStripePaymentListUiModel);
      }
    }
    if (retrieveStripePaymentListFault) {
      setStripePaymentListUiModel(defaultStripePaymentListUiModel);
    }
  }, [retrieveStripePaymentListUiModel, retrieveStripePaymentListFault]);

  useEffect(() => {
    if (defaultValidPI && isCreatePaymentInstrument) {
      setIsCreatePaymentInstrument(false);
      if (createPaymentInstrumentCoqResponseStatus) {
        completeOrder3DSHandler();
      }
    }
  }, [defaultValidPI]);

  useEffect(() => {
    if (
      (subscriber || retrieveSubscriberResponse) &&
      retrieveDefaultPaymentResponse &&
      stripePaymentListUiModel
    ) {
      if (retrieveSubscriberResponse || subscriber) {
        const showAdditionalSubscriberDetailsStatus =
          shouldWeShowAdditionalSubscriberDetails(
            subscriber || retrieveSubscriberResponse?.Subscriber,
          );
        setShowAdditionalSubscriberDetails(
          showAdditionalSubscriberDetailsStatus,
        );
        setCurrentSubscriberData(
          subscriber || retrieveSubscriberResponse?.Subscriber,
        );
      }
      const paymentInstrumentNodes = getPaymentInstrumentListNode(
        retrieveDefaultPaymentResponse,
      );
      const isStripePayment = paymentInstrumentNodes?.[0]?.StripeAccount;
      const isStripeEnabled = stripePaymentListUiModel?.StripePayment;
      const paymentInstruments =
        (isStripeEnabled && !isStripePayment) ||
        (!isStripeEnabled && isStripePayment)
          ? []
          : paymentInstrumentNodes;
      setDefaultValidPI(paymentInstruments);
    }
  }, [
    retrieveSubscriberResponse,
    subscriber,
    retrieveDefaultPaymentResponse,
    stripePaymentListUiModel,
  ]);

  useEffect(() => {
    if (
      errorFaultCode ||
      calculateOrderQuoteFault ||
      retrieveProductContextFault ||
      subscriptionUiFault
    ) {
      if (errorFaultCode) {
        const errorNode = errorCodeConstant?.some(
          (errorCodeItem) => errorCodeItem === errorFaultCode,
        );
        setIsErrorAlertDisplay(errorNode);
        setIsGenericErrorAlertDisplay(!errorNode);
      } else {
        if (
          !calculateOrderQuoteFault?.includes(voucherName) ||
          retrieveProductContextFault
        ) {
          setIsGenericErrorAlertDisplay(true);
          setIsReady(true);
          setIsPaymentReady(true);
        } else {
          setIsErrorAlertDisplay(false);
          setIsGenericErrorAlertDisplay(false);
        }
      }
      setSubmitOrderCountryUpdateFaultMessage('');
    }
  }, [
    errorFaultCode,
    calculateOrderQuoteFault,
    retrieveProductContextFault,
    subscriptionUiFault,
  ]);

  useEffect(() => {
    if (requestProductDataUiModel?.metadata) {
      const productId = requestProductDataUiModel?.metadata?.Product.Id;
      const getPricingPlanExternalReference =
        upgradePlanState?.pricingPlanExternalReference
          ? upgradePlanState?.pricingPlanExternalReference
          : pricingPlanExternalReference;
      const pricingPlan =
        requestProductDataUiModel.metadata.Product.PricingPlans.filter(
          (pricingPlanItem) =>
            pricingPlanItem.ExternalReferences?.some((externalReferenceItem) =>
              Object.values(externalReferenceItem).includes(
                getPricingPlanExternalReference,
              ),
            ),
        );

      pricingPlan.forEach((pricingPlanItem) => {
        setOrderablePricingPlanId(pricingPlanItem.Id);
      });
      if (!pricingPlan.length) {
        defaultSubscriptionHandlers();
      }
      const payload = {
        IncludeEntitlementContext: true,
        IncludeOrderablePricingPlans: true,
        ProductId: productId,
      };
      requestRetrieveProductContext(payload);
    }
  }, [requestProductDataUiModel]);

  useEffect(() => {
    if (
      retrieveProductContextResponse &&
      defaultValidPI &&
      orderablePricingPlanId
    ) {
      setHasRPCresponse(true);
      const { OrderablePricingPlans, ProductId } =
        retrieveProductContextResponse?.retrieveProductContext?.ProductContext;
      if (OrderablePricingPlans?.length) {
        const orderablePricingPlan = OrderablePricingPlans.filter(
          (orderablePricingPlanItem) =>
            orderablePricingPlanItem.Id === orderablePricingPlanId,
        );
        if (orderablePricingPlan?.length) {
          setHasOrderablePricingPlans(true);
          const shoppingCartPayload = orderablePricingPlan?.map(
            (orderablePricingPlanItem) => {
              const { Currency, Amount, DiscountedAmount, Id } =
                orderablePricingPlanItem;
              const modifiedDate = new Date();
              const ISOdateString = modifiedDate.toISOString();
              const shoppingCart = {
                ShoppingCart: {
                  Currency,
                  GrossAmount: Amount,
                  DiscountAmount: DiscountedAmount,
                  DepositAmount: 0,
                  TotalAmount: Amount,
                  PaymentInstrumentRequired: true,
                  AppliedCoupons: '',
                  Modified: ISOdateString,
                  Items: [
                    {
                      PricingPlanId: Id,
                      ProductId: ProductId,
                      Quantity: 1,
                    },
                  ],
                },
              };
              setShoppingCartList(shoppingCart);
              return shoppingCart;
            },
          );
          const { ShoppingCart } = shoppingCartPayload[0] || {};
          setDefaultPaymentResponse({
            paymentInstrumentIds: defaultValidPI,
            isPaymentInstrumentIdUpdate: false,
          });
          requestCalculateOrderQuote({
            ShoppingCart,
            paymentInstruments: defaultValidPI,
            redemptionCodesPayload,
            UseDefaults: false,
            isUpgrade: upgradePlanState?.isUpgradePlan,
            ...getExistingSubscriptionForUpgrade(upgradePlanState),
          });
        } else {
          defaultSubscriptionHandlers();
        }
      } else {
        defaultSubscriptionHandlers();
      }
    }
  }, [retrieveProductContextResponse, defaultValidPI, orderablePricingPlanId]);

  useEffect(() => {
    if (calculateOrderQuoteResponse) {
      const translatedOrderItemsList = getTranslatedOrderItemsList(
        calculateOrderQuoteResponse,
        requestProductDataUiModel?.metadata?.Product,
      );
      setTranslatedOrderItems(translatedOrderItemsList);
      setHasSuccessFulCOQResponse(true);
      calculateOrderQuoteResponseList({ ...calculateOrderQuoteResponse });
      if (isCoqResponseShowLoader === false) {
        postMessage('module.hideLoader', {});
      }
      translatedOrderItemsList.forEach((translatedOrderItem) => {
        if (
          translatedOrderItem?.isFreeTrial ||
          translatedOrderItem?.introPricing?.isIntroPricing
        ) {
          setDisableVoucherCode(true);
        }
      });
      setIsReady(true);
      if (isGenericErrorAlertDisplay) {
        setIsGenericErrorAlertDisplay(false);
      }
    }
  }, [calculateOrderQuoteResponse]);

  useEffect(() => {
    if (calculateOrderQuoteFault) {
      if (calculateOrderQuoteFault.includes(voucherName)) {
        setVoucherCodeCredentialError(calculateOrderQuoteFault);
        setIsVoucherCodeError(true);
      } else {
        const translatedGenericOrderItemsList = getGenericTranslatedOrderItems(
          requestProductDataUiModel.metadata,
          shoppingCartList?.ShoppingCart?.Currency,
        );

        const getDefaultOrderQuoteList =
          getDefaultShoppingCart(shoppingCartList);

        setDefaultShoppingCartQuote({
          Quote: getDefaultOrderQuoteList,
        });
        setTranslatedOrderItems(translatedGenericOrderItemsList);
        setHasSuccessFulCOQResponse(false);
        setErrors((prevState) => [...prevState, calculateOrderQuoteFault]);
        calculateOrderQuoteFaultNode(
          calculateOrderQuoteFault,
          calculateOrderQuoteFaultCode,
        );
      }
      setIsReady(true);
    }
  }, [calculateOrderQuoteFault]);

  useEffect(() => {
    if (retrieveConfigCurrencyJSONResponse && retrieveEnvironment) {
      const { orderQuote } = calculateOrderQuoteResponse;
      const {
        Quote: {
          Currency,
          Totals: { TotalAmount },
        },
      } = orderQuote;

      const orderQuoteCurrency = getOrderQuoteUtilities.getCurrency(orderQuote);

      const { currency } = retrieveConfigCurrencyJSONResponse;
      const config3DSenv = currency[retrieveEnvironment];
      const is3DSenabled = config3DSenv[orderQuoteCurrency];

      if (is3DSenabled) {
        postMessage('module.showLoader', {});
        getSetup3DSWeb(() => {
          const payload = {
            Currency,
            OrderAmount: TotalAmount,
          };
          requestRetrieve3DSToken(payload);
        });
      } else {
        postMessage('module.showLoader', {});
        const submitOrderUpdateCountryPayload = submitOrderHandler();

        requestSubmitOrderUpdateCountry({
          submitOrderUpdateCountryPayload,
          staticApiKey,
          staticApiUrl,
          isUpgrade: upgradePlanState?.isUpgradePlan,
          ...getExistingSubscriptionForUpgrade(upgradePlanState),
        });
      }
    }
  }, [retrieveConfigCurrencyJSONResponse, retrieveEnvironment]);

  useEffect(() => {
    if (retrieve3DSTokenResponse) {
      const token = retrieve3DSTokenResponse?.tokenResponse?.Token;

      const callbackParams = (threeDSReferenceId) => {
        const ThreeDSDetail = {
          ThreeDSReferenceId: threeDSReferenceId,
        };
        const submitOrderUpdateCountryPayload = {
          ...submitOrderHandler(),
          ThreeDSDetail,
        };
        postMessage('module.showLoader', {});
        requestSubmitOrderUpdateCountry({
          submitOrderUpdateCountryPayload,
          staticApiKey,
          staticApiUrl,
          isUpgrade: upgradePlanState?.isUpgradePlan,
          ...getExistingSubscriptionForUpgrade(upgradePlanState),
        });
      };

      getInitialize3DSWeb(
        token,
        retrieveDefaultPaymentResponse,
        callbackParams,
        submitOrder3DSHandler,
        requestSubmitOrderUpdateCountry,
        threeDSpaymentError6170124,
        staticApiKey,
        staticApiUrl,
        setShowBackdrop,
      );
    }
  }, [retrieve3DSTokenResponse]);

  useEffect(() => {
    if (
      updateSubscriberResponse &&
      showAdditionalSubscriberDetails &&
      completeOrder3DSstatus
    ) {
      postMessage('module.hideLoader', {});
      requestConfigCurrencyJSON();
    }
  }, [updateSubscriberResponse, showAdditionalSubscriberDetails]);

  const getPaymentInstrumentFromOrder = () =>
    submitOrderUpdateCountryResponse?.submitOrderUpdateCountryResult?.Order
      ?.PaymentInstruments[0]?.PaymentInstrument;

  const updateLegacySubscriptions = (paymentInstrument) => {
    if (allSubscriptions && allSubscriptions.length > 0) {
      const subscriptionsToUpdate = allSubscriptions.filter(
        (candidate) =>
          candidate?.PaymentInstrument?.Type ===
          paymentInstrumentTypes.creditCard,
      );
      if (subscriptionsToUpdate.length > 0) {
        const newPaymentInstrumentId = paymentInstrument?.Id;
        const existingLegacyPaymentInstrumentIds = [];
        subscriptionsToUpdate.forEach((subscription) => {
          if (
            existingLegacyPaymentInstrumentIds.indexOf(
              subscription?.PaymentInstrument?.Id,
            ) < 0
          ) {
            existingLegacyPaymentInstrumentIds.push(
              subscription?.PaymentInstrument?.Id,
            );
          }
        });
        setPaymentInstrumentsToRemove(existingLegacyPaymentInstrumentIds);

        const updateRequests = [];
        subscriptionsToUpdate.forEach((subscription) => {
          updateRequests.push({
            Id: subscription?.Id,
            PaymentInstrumentId: newPaymentInstrumentId,
          });
        });
        requestUpdateMultipleSubscriptions(updateRequests);
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (submitOrderUpdateCountryResponse) {
      const paymentInstrument = getPaymentInstrumentFromOrder(
        submitOrderUpdateCountryResponse,
      );
      const isStripePayment = !!paymentInstrument?.StripeAccount;
      if (isStripePayment) {
        const updatingLegacySubscriptions =
          updateLegacySubscriptions(paymentInstrument);
        if (!updatingLegacySubscriptions) {
          setSubscriptionCheckInProgress(true);
        }
      } else {
        setSubscriptionCheckInProgress(true);
      }
    }
  }, [submitOrderUpdateCountryResponse]);

  const [paymentInstrumentsToRemove, setPaymentInstrumentsToRemove] = useState(
    [],
  );
  useEffect(() => {
    if (updateMultipleSubscriptionUiModel) {
      if (paymentInstrumentsToRemove && paymentInstrumentsToRemove.length > 0) {
        const payloads = [];
        paymentInstrumentsToRemove.forEach((paymentInstrumentId) => {
          payloads.push({ Id: paymentInstrumentId });
        });
        requestRemoveMultiplePaymentInstruments(payloads);
      } else {
        setSubscriptionCheckInProgress(true);
      }
    }
  }, [updateMultipleSubscriptionUiModel, paymentInstrumentsToRemove]);

  useEffect(() => {
    if (removeMultiplePaymentInstrumentsUiModel) {
      setSubscriptionCheckInProgress(true);
    }
  }, [removeMultiplePaymentInstrumentsUiModel]);

  useEffect(() => {
    if (removeMultiplePaymentInstrumentsFault) {
      setSubscriptionCheckInProgress(true);
    }
  }, [removeMultiplePaymentInstrumentsFault]);

  useEffect(() => {
    if (updateMultipleSubscriptionFault) {
      setIsErrorAlertDisplay(true);
      setIsMerchantEventHandlerFault(true);
      setErrors([...errors, updateMultipleSubscriptionFault]);
    }
  }, [updateMultipleSubscriptionFault]);

  const subscriptionFound = () => {
    if (upgradePlanState?.isUpgradePlan) {
      postMessage(
        'module.orderUpgradeComplete',
        submitOrderUpdateCountryResponse,
      );
    } else {
      postMessage('order.complete', submitOrderUpdateCountryResponse);
    }
    setSubscriptionCheckInProgress(false);
    setOrderComplete(true);
    // postMessage('module.hideLoader', {});
  };

  const subscriptionCheckFailure = () => {
    postMessage('module.error');
    setSubscriptionCheckInProgress(false);
    postMessage('module.hideLoader', {});
  };

  useEffect(() => {
    if (submitOrderUpdateCountryFault) {
      postMessage('module.hideLoader', {});
      if (submitOrderUpdateCountryFault?.Fault?.Code === 635) {
        const threeDSCompletionDetails = {
          PaymentCompletionURL: getSubmitOrderResponseContextData(
            submitOrderUpdateCountryFault,
            'PaymentCompletionURL',
          ),
          PaymentCompletionToken: getSubmitOrderResponseContextData(
            submitOrderUpdateCountryFault,
            'PaymentCompletionToken',
          ),
          PaymentAuthenticationTransactionId: getSubmitOrderResponseContextData(
            submitOrderUpdateCountryFault,
            'PaymentAuthenticationTransactionId',
          ),
        };
        // Show cybersource challenge window
        postMessage('module.showBackdrop');
        if (setShowBackdrop) {
          setShowBackdrop(true);
        }
        document.body.classList.add('challange-window');
        getThreeDSChallengeHandler(threeDSCompletionDetails);
      } else {
        setSubmitOrderCountryUpdateFaultMessage(submitOrderUpdateCountryFault);
      }
    }
  }, [submitOrderUpdateCountryFault]);

  useEffect(() => {
    const filterFaults = requestFaults.filter((fault) => !!fault);
    if (filterFaults.length) {
      const currentFault = filterFaults[0];
      setErrors([...errors, currentFault]);
      postMessage('module.hideLoader', {});
      setIsReady(true);
      setIsPaymentReady(true);
    }
  }, [...requestFaults]);

  useEffect(() => {
    if (updateSubscriberFault) {
      postMessage('module.hideLoader', {});
      setUpdateSubscriberFaultMsg(updateSubscriberFault);
    }
  }, [updateSubscriberFault]);

  const requestCalculateOrderQuoteBasedOnDefaultSelection = (
    paymentInstrumentId,
  ) => {
    setDefaultPaymentResponse({
      paymentInstrumentIds: [paymentInstrumentId],
      isPaymentInstrumentIdUpdate: true,
    });
    const { ShoppingCart } = shoppingCartList;
    requestCalculateOrderQuote({
      ShoppingCart,
      paymentInstruments: [paymentInstrumentId],
      redemptionCodesPayload,
      isUpgrade: upgradePlanState?.isUpgradePlan,
      ...getExistingSubscriptionForUpgrade(upgradePlanState),
    });
  };

  const setDefaultPayment = (
    paymentInstrumentId,
    createPaymentInstrument = null,
    creditToStripePaymentResponse = [],
  ) => {
    if (createPaymentInstrument) {
      setIsCreatePaymentInstrument(true);
      setIsCoqResponseShowLoader(true);
      requestCalculateOrderQuoteBasedOnDefaultSelection(
        createPaymentInstrument,
      );
      return;
    }
    const defaultPaymentList = creditToStripePaymentResponse.length
      ? creditToStripePaymentResponse
      : defaultValidPI;
    const getDefaultPaymentBasedOnIdActivity = (defaultPaymentListNode) =>
      (defaultPaymentListNode || []).find(
        (item) => item.Id === paymentInstrumentId,
      );
    const getDefaultPaymentBasedOnId =
      getDefaultPaymentBasedOnIdActivity(defaultPaymentList);
    setCalculateOrderQuoteFaultNodeForPayment('');
    getDefaultPaymentBasedOnId &&
      requestCalculateOrderQuoteBasedOnDefaultSelection(
        getDefaultPaymentBasedOnId,
      );
  };

  const addressAndCodeTableFaultNode = (faultNode) => {
    setAddressAndCodeTableFault(faultNode);
    setErrors([...errors, faultNode]);
  };

  const completeOrder3DSHandler = () => {
    if (showAdditionalSubscriberDetails) {
      setSendUpdateSubscriberResponse(false);
      setCompleteOrder3DSstatus(true);
      formik.handleSubmit();
      postMessage('module.hideLoader', {});
      return;
    }
    requestConfigCurrencyJSON();
  };

  const threeDSpaymentError6170124 = () => {
    const submitOrderError617 = {
      Code: 617,
      Subcode: 6170124,
    };
    const errorMsg = translateFault(submitOrderError617)?.translatedMessage;
    setIsErrorAlertDisplay(true);
    setIsMerchantEventHandlerFault(true);
    setErrors([...errors, errorMsg]);
  };

  const updateSubscriberFaultHandler = (updateSubscriberFault) => {
    setUpdateSubscriberFaultMsg(updateSubscriberFault);
  };

  const resetFormHandler = () => {
    setIsPaymentMethodFormError(false);
  };

  useEffect(() => {
    if (isReady && isPaymentReady) {
      postMessage('module.hideLoader');
    }
  }, [isReady, isPaymentReady]);

  /** Loader */
  if (subscriptionCheckInProgress) {
    return (
      <>
        <SubscriptionCheck
          callback={subscriptionFound}
          faultCallback={subscriptionCheckFailure}
          onFaultChange={handleFaultFromChild}
          submitOrderUpdateCountryResponse={submitOrderUpdateCountryResponse}
        ></SubscriptionCheck>
      </>
    );
  }

  let orderContainsFreeTrial = false;
  translatedOrderItems?.forEach((orderItem) => {
    if (orderItem.isFreeTrial) {
      orderContainsFreeTrial = true;
    }
  });

  if (orderComplete) {
    return null;
  }

  return (
    <Box className={isReady && isPaymentReady ? '' : classes.hidden}>
      {/*subscriptionCheckInProgress && (
        <SubscriptionCheck
          callback={subscriptionFound}
          faultCallback={subscriptionCheckFailure}
        ></SubscriptionCheck>
      )*/}
      {upgradePlanState?.pathFrom && (
        <Link
          className={classes.link}
          style={{
            display: 'flex',
            borderBottom: 'none',
            cursor: 'pointer',
            marginBottom: '20px',
          }}
          underline="none"
          onClick={() => {
            push({
              pathname: '/my-subscription',
            });
            postMessage('module.backButton', {});
          }}
        >
          <Typography
            color="primary"
            className={classes.backArrow}
            style={{ marginRight: '4px' }}
          >
            <img
              src={backArrow}
              alt={t(
                LocaleKeys.subscriptionInfo.subscriptions.backToMySubscription,
              )}
            />
          </Typography>

          <Typography color="textPrimary" variant="h6" className={classes.back}>
            {t(LocaleKeys.subscriptionInfo.subscriptions.backToMySubscription)}
          </Typography>
        </Link>
      )}
      {upgradePlanState?.isUpgradePlan ? (
        <Box style={{ marginBottom: '10px' }}>
          <Typography variant="h2">
            {t(
              LocaleKeys.subscriptionInfo.subscriptions.reviewYourUpgradeOrder,
            )}
          </Typography>
        </Box>
      ) : (
        <Box className={classes.orderPanelHeading}>
          <Typography variant="h2" className={classes.headerText}>
            {t(LocaleKeys?.payment?.orders.orderTitle)}
          </Typography>
        </Box>
      )}
      {errors && errors.length > 0 ? (
        <Box className={classes.subscriptionErrorContainer}>
          <ErrorScreen
            error={errors[errors.length - 1]}
            errors={errors}
            mt={5}
            mb={5}
            variant="body1"
            hideBackdrop={true}
          />
        </Box>
      ) : null}
      {subscriptionFault && (
        <Box mb={6}>
          <ErrorScreen
            error={subscriptionFault}
            mt={5}
            mb={5}
            variant="body1"
            hideBackdrop={true}
          />
        </Box>
      )}
      {!addressAndCodeTableFault && !subscriptionFault && (
        <Box>
          {(!orderablePricingPlanId ||
            (hasRPCresponse && !hasOrderablePricingPlans)) &&
            isReady && <NoOrderablePricingPlans />}
          {hasRPCresponse && hasOrderablePricingPlans && (
            <>
              {orderContainsFreeTrial && <FreeTrialHeader />}
              {upgradePlanState?.isUpgradePlan ? (
                <OrderDetailsUpgradePricingPlan
                  upgradePlanState={upgradePlanState}
                  calculateOrderQuoteResponse={calculateOrderQuoteResponse}
                  translatedOrderItems={translatedOrderItems}
                />
              ) : (
                <OrderDetailsContainer
                  translatedOrderItems={translatedOrderItems}
                  hasSuccessFulCOQResponse={hasSuccessFulCOQResponse}
                  hideModifyOrderLink={upgradePlanState?.pathFrom === PURCHASE}
                />
              )}
              <Box>
                <Divider className={classes.divider} />
                {!disableVoucherCode &&
                  (showVoucherCode ? (
                    <>
                      <VoucherCode
                        language={language}
                        handleVoucherSaveClick={handleVoucherSaveClick}
                        handleVoucherCancelClick={handleVoucherCancelClick}
                        credentialErrorMessage={voucherCodeCredentialError}
                      />
                      <Divider className={classes.divider} />
                    </>
                  ) : (
                    <>
                      <AppliedVoucherCode
                        removeAppliedVoucherCode={removeAppliedVoucherCode}
                        appliedCouponsText={appliedCouponsText}
                      />
                      <Divider className={classes.divider} />
                    </>
                  ))}
                {showAdditionalSubscriberDetails && (
                  <Box id="additionalSubscriberDetails">
                    <FirstNameField
                      form={formik}
                      name="FirstName"
                      resetFormStatus={true}
                      resetFormHandler={resetFormHandler}
                    />
                    <LastNameField
                      form={formik}
                      name="LastName"
                      resetFormStatus={true}
                      resetFormHandler={resetFormHandler}
                    />
                    <DateOfBirthField
                      form={formik}
                      name="DateOfBirth"
                      resetFormStatus={true}
                      resetFormHandler={resetFormHandler}
                    />
                  </Box>
                )}
                {updateSubscriberFaultMsg && (
                  <>
                    <ErrorScreen
                      error={updateSubscriberFaultMsg}
                      mt={5}
                      mb={5}
                      variant="body1"
                      hideBackdrop={true}
                      errorDialog={false}
                      removeStyle={true}
                    />
                  </>
                )}
                <PaymentMethodContainer
                  staticApiKey={staticApiKey}
                  staticApiUrl={staticApiUrl}
                  stripePromise={stripePromise}
                  userCountry={userCountry}
                  paymentInstruments={defaultValidPI}
                  defaultPaymentInstruments={retrieveDefaultPaymentResponse.filter(
                    (item) => item.Default,
                  )}
                  setDefaultPayment={setDefaultPayment}
                  shoppingCartList={shoppingCartList}
                  calculateOrderQuoteResponseForPayment={
                    calculateOrderQuoteResponseForPayment
                  }
                  calculateOrderQuoteFaultNodeForPayment={
                    calculateOrderQuoteFaultNodeForPayment
                  }
                  isErrorAlertDisplay={isErrorAlertDisplay}
                  errors={errors}
                  addressAndCodeTableFaultNode={addressAndCodeTableFaultNode}
                  completeOrder3DSHandler={completeOrder3DSHandler}
                  submitOrderUpdateCountryFaultMessage={
                    submitOrderCountryUpdateFaultMessage
                  }
                  submitOrderUpdateCountryFault={submitOrderUpdateCountryFault}
                  isGenericErrorAlertDisplay={isGenericErrorAlertDisplay}
                  isMerchantEventHandlerFault={isMerchantEventHandlerFault}
                  isVoucherCodeError={isVoucherCodeError}
                  submitOrderUpdateCountryResponse={
                    submitOrderUpdateCountryResponse
                  }
                  requestSubmitOrderUpdateCountry={
                    requestSubmitOrderUpdateCountry
                  }
                  stripePaymentList={stripePaymentListUiModel}
                  defaultOrderQuote={
                    calculateOrderQuoteResponse?.orderQuote ||
                    defaultShoppingCartQuote
                  }
                  defaultPaymentResponse={defaultPaymentResponse}
                  redemptionCodesPayload={redemptionCodesPayload}
                  setSubmitOrderCountryUpdateFaultMessage={
                    setSubmitOrderCountryUpdateFaultMessage
                  }
                  setShowBackdrop={setShowBackdrop}
                  formik={formik}
                  showAdditionalSubscriberDetails={
                    showAdditionalSubscriberDetails
                  }
                  updateSubscriberFaultHandler={updateSubscriberFaultHandler}
                  updateSubscriberResponse={
                    sendUpdateSubscriberResponse && updateSubscriberResponse
                  }
                  setSubmitStripeOrNonStripeCardErrorStatus={
                    setSubmitStripeOrNonStripeCardErrorStatus
                  }
                  isPaymentMethodFormError={isPaymentMethodFormError}
                  setIsPaymentMethodFormError={setIsPaymentMethodFormError}
                  isPaymentReady={isPaymentReady}
                  setIsPaymentReady={setIsPaymentReady}
                  submitOrderInteractionData={submitOrderInteractionData}
                  additionalSubscriberFormErrors={formik.errors}
                  allSubscriptions={allSubscriptions}
                  upgradePlanState={upgradePlanState}
                />
              </Box>
            </>
          )}
        </Box>
      )}
      <Backdrop
        isVisible={showBackdrop}
        isStripe={stripePaymentListUiModel?.StripePayment}
      />
    </Box>
  );
}

export default compose(withTranslation())(CheckoutContainer);
